import React, { useState } from 'react';
import cx from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { FontAwesomeIcon } from '@magoosh/lib/icons';
import AudioSetup from 'types/audio_setup';
import fetch from 'utilities/fetch';
import { paths } from 'config/path_helpers';

import AudioRecorder from '../../recorder';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss'

interface Props {
  onError: () => void;
  onFinish: () => void;
  onRetry: () => void;
  onSuccess: () => void;
}

export default (props: Props) => {
  const [audioSetup, setAudioSetup] = useState<AudioSetup>(null);

  const handleBlobAvailable = (blob: Blob) => {
    // Optimistically set to processing for quick UI feedback to the user
    setAudioSetup({
      audio: null,
      id: uuidv4(),
      status: 'processing'
    });

    return fetch(paths.api.audioSetups(), {
      body: JSON.stringify({ audioSetup: { audioFile: blob } }),
      method: 'POST'
    }).then((response) => {
      setAudioSetup(response.audioSetup);

      if (response.audioSetup.status === 'success') {
        props.onSuccess();
      } else if (response.audioSetup.status === 'error') {
        props.onError();
      }
    }).catch((error) => {
      setAudioSetup({ ...audioSetup, status: 'error' });
      props.onError();
    });
  };

  const tryAgain = () => {
    setAudioSetup(null);
    props.onRetry();
  };
  
  if (audioSetup?.status === 'processing') {
    return (
      <div className={cx(
        style.recorderWell,
        'align-items-center d-flex flex-column justify-content-center u-padding-A-s'
      )}>
        {/* Padding the match the AudioRecorder */}
        <em style={{ paddingBottom: 19 }}>Analyzing your recording</em>
        <FontAwesomeIcon icon="microphone" style={{ color: colors.grayLight, fontSize: 40 }} />
      </div>
    );
  } else if (audioSetup?.status === 'error') {
    return (
      <div className={cx(
        style.recorderWell,
        style.error,
        'align-items-center d-flex flex-column gap-s justify-content-center u-padding-A-s'
      )}>
        <div className="text-bold text-danger">
          <FontAwesomeIcon icon="exclamation-triangle" /> There was an issue with your recording. <a className="text-danger" onClick={props.onFinish}>Finish anyway</a>.
        </div>

        {
          audioSetup.audio && (
            <audio
              className="d-flex"
              controls
              src={audioSetup.audio.url}
            >
              Your browser does not support the audio element.
            </audio>
          )
        }
      </div>
    );
  } else if (audioSetup?.status === 'success') {
    return (
      <div className={cx(
        style.recorderWell,
        style.success,
        'align-items-center d-flex flex-column gap-s justify-content-center u-padding-A-s'
      )}>
        <div className="text-bold text-success">
          <FontAwesomeIcon icon="check" /> Here's What We Heard. <a className="text-success" onClick={tryAgain}>Try again</a>.
        </div>

        <div className="text-center">"{audioSetup.audio.transcript}"</div>

        <audio
          className="d-flex"
          controls
          src={audioSetup.audio.url}
        >
          Your browser does not support the audio element.
        </audio>
      </div>
    );
  } else {
    return (
      <AudioRecorder
        className={cx(style.recorderWell, 'align-items-center d-flex flex-column justify-content-center')}
        onBlobAvailable={handleBlobAvailable}
        prepTime={0}
        recordingTime={10}
      />
    );
  }
};
